import React from 'react';
import ReactDOM from 'react-dom/client';
import { Provider } from 'react-redux';
import App from './App';
import configureStore from './store';

// Modals in this app are owned by react-bootstrap (state-driven), but stale
// `data-bs-toggle="modal"` / `data-bs-target` attributes are still present on
// many buttons. Bootstrap's bundled data-API listens on `document` and crashes
// when its target selector doesn't resolve.
//
// Belt-and-suspenders neutralizer:
//   1) A body-level bubble listener stops the click from reaching Bootstrap's
//      delegated `document` handler in the normal case.
//   2) A prototype patch on `bootstrap.Modal` short-circuits the constructor
//      and `_isAnimated` when the element is null, so any click that does
//      slip through still cannot throw `Cannot read properties of undefined
//      (reading 'classList')`.
if (typeof document !== 'undefined') {
    const swallowStaleModalClick = (event) => {
        const trigger = event.target?.closest?.('[data-bs-toggle="modal"]');
        if (!trigger) return;
        const selector = trigger.getAttribute('data-bs-target') || trigger.getAttribute('href');
        const targetEl = selector ? document.querySelector(selector) : null;
        if (!targetEl) event.stopPropagation();
    };
    document.body.addEventListener('click', swallowStaleModalClick, false);

    /* eslint-disable no-underscore-dangle, func-names */
    const patchBootstrapModal = () => {
        const ModalCtor = typeof window !== 'undefined' && window.bootstrap && window.bootstrap.Modal;
        if (!ModalCtor || ModalCtor.prototype.__nullSafePatched) return true;
        const proto = ModalCtor.prototype;

        const originalIsAnimated = proto._isAnimated;
        if (typeof originalIsAnimated === 'function') {
            proto._isAnimated = function safeIsAnimated() {
                if (!this || !this._element || !this._element.classList) return false;
                return originalIsAnimated.call(this);
            };
        }

        const originalInitBackDrop = proto._initializeBackDrop;
        if (typeof originalInitBackDrop === 'function') {
            proto._initializeBackDrop = function safeInitializeBackDrop() {
                if (!this || !this._element) return null;
                return originalInitBackDrop.call(this);
            };
        }

        ['show', 'hide', 'toggle', 'dispose', 'handleUpdate'].forEach((methodName) => {
            const original = proto[methodName];
            if (typeof original !== 'function') return;
            proto[methodName] = function safeModalMethod(...args) {
                if (!this || !this._element) return undefined;
                return original.apply(this, args);
            };
        });

        proto.__nullSafePatched = true;
        return true;
    };
    /* eslint-enable no-underscore-dangle, func-names */
    if (!patchBootstrapModal()) {
        window.addEventListener('load', patchBootstrapModal, { once: true });
        document.addEventListener('DOMContentLoaded', patchBootstrapModal, { once: true });
    }
}

const store = configureStore();

const root = ReactDOM.createRoot(document.getElementById('root'));
root.render(
    <Provider store={store}>
        <App />
    </Provider>
);
